import { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { TerminalContext, TerminalContextInterface } from '../../../TerminalInit';
import { Contact } from '../../../api/contacts';
import { SpotLayoutItem, useSpotLayoutItems } from '../../../api/spotLayoutItems';
import { useSpotLayout } from '../../../api/spotLayouts';
import { useSpot } from '../../../api/spots';
import { Transaction, TransactionType } from '../../../common/transactions';
import useTransactionsWithSlots from '../../../hooks/useTransactionsWithSlots';
import SlotsOverview from '../../../workflow/SlotsOverview';
import LoadingView from '../../common/LoadingView';

interface TransactionsByTypeOverviewProps {
    filterObject?: JSX.Element;
    transactionTypeFilter?: TransactionType[];
    onHome?: () => void;
    onBack?: () => void;
    onInactivity?: () => void;
    loggedInContact?: Contact;

    cancelTransaction?: (transaction: Transaction) => void;
    changeTransactionSlot?: (transaction: Transaction) => void;
    removeTransaction?: (transaction: Transaction) => void;
}

const TransactionsByTypeOverview = (props: TransactionsByTypeOverviewProps) => {
    const { terminal } = useContext<TerminalContextInterface>(TerminalContext);
    const { data: spotLayout } = useSpotLayout(terminal?.spot_layout_url);
    const { data: spot } = useSpot(spotLayout?.spot_url);
    const { data: spotLayoutItems } = useSpotLayoutItems({ spotLayout: spotLayout }, { enabled: !!spotLayout });
    const { data: slotsMap } = useTransactionsWithSlots({ spotId: spot?.id });

    const [highlightedItems, changeHighlightedItems] = useState<SpotLayoutItem[] | undefined>();
    useEffect(() => {
        if (props.transactionTypeFilter === undefined || props.transactionTypeFilter.length === 0) {
            changeHighlightedItems([]);
            return;
        }
        if (props.transactionTypeFilter) {
            changeHighlightedItems(
                spotLayoutItems?.filter((item) => {
                    if (item.slot) {
                        const slotWithTransactions = slotsMap?.get(item.slot);
                        return slotWithTransactions !== undefined
                            ? slotWithTransactions.some((swt) => props.transactionTypeFilter!.includes(swt.transaction.type))
                            : false;
                    }
                    return false;
                })
            );
        }
    }, [spotLayoutItems, props.transactionTypeFilter, slotsMap]);

    if (slotsMap) {
        return (
            <SlotsOverview
                filterObject={props.filterObject}
                transactionTypeFilter={props.transactionTypeFilter}
                onInactivity={props.onInactivity}
                onHome={props.onHome}
                onBack={props.onBack}
                transactions={slotsMap}
                selectableItems={spotLayoutItems}
                highlightedItems={highlightedItems}
                loggedInContact={props.loggedInContact}
                title={
                    <FormattedMessage
                        id='management.TransactionByTypeOverview.title'
                        description='This is the message displayed on the TransactionByTypeOverview'
                        defaultMessage='Slot transactions overview'
                    />
                }
                cancelTransaction={props.cancelTransaction}
                changeTransactionSlot={props.changeTransactionSlot}
                removeTransaction={props.removeTransaction}
            />
        );
    }
    return (
        <LoadingView
            onInactivity={props.onInactivity}
            onHome={props.onHome}
            onBack={props.onBack}
        />
    );
};

export default TransactionsByTypeOverview;
